/**
 * TableUploadReport — what a bulk upload actually did, and a way to finish it.
 *
 * A folder of 180 documents can lose files for three unrelated reasons, and
 * they need three different answers from the reader:
 *
 *   skipped   the server refused the content. Nothing to retry — the reason is
 *             the useful part, so every reason is shown.
 *   failed    the upload itself did not land. Worth retrying, and that is the
 *             one button on this panel.
 *   processing a batch uploaded but was not accepted for AI processing. The
 *             rows exist; they are just not queued. Retrying the UPLOAD would
 *             duplicate them, so this is stated and not offered as a retry.
 *
 * All of this was already computed and then thrown away: the run produced 160
 * failed file names and the user was shown "Some documents failed to upload."
 * A count with no names cannot be acted on.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiCheckCircleLight,
  PiWarningCircleLight,
  PiXCircleLight,
  PiArrowsClockwiseLight,
  PiHourglassLight,
} from 'react-icons/pi';
import Button from '../shared/TailwindComponents/Button';
import Badge from '../shared/TailwindComponents/Badge';
import { Well } from '../shared/brand/Field';
import { SkippedFile } from '../../services/uploads/uploadBatching';

export interface TableUploadReportData {
  tableUuid: string;
  /** Files handed to the run. */
  attempted: number;
  /** Files the server confirmed it stored. */
  uploadedCount: number;
  skipped: SkippedFile[];
  /** Names of files whose upload never landed. */
  failed: string[];
  /** The `File` objects behind `failed`, when they could be matched back. */
  retryFiles: File[];
  /** Batches that uploaded but whose rows were not queued for processing. */
  processingFailedBatches: number;
  /** The server's own words, when it gave any. */
  serverError?: string;
}

interface Props {
  data: TableUploadReportData;
  onRetry: (files: File[]) => void;
  onClose: () => void;
}

/** One counted outcome, as a row. */
const Tally: React.FC<{
  icon: React.ReactNode;
  label: string;
  count: number;
  tone: 'success' | 'warning' | 'danger';
}> = ({ icon, label, count, tone }) => {
  const TONE = {
    success: 'text-success-600 dark:text-success-400',
    warning: 'text-warning-600 dark:text-warning-400',
    danger: 'text-danger-600 dark:text-danger-400',
  } as const;
  return (
    <div className="flex items-center gap-2">
      <span className={`flex-shrink-0 [&>svg]:h-4 [&>svg]:w-4 ${TONE[tone]}`}>{icon}</span>
      <span className="text-sm text-fg-body">{label}</span>
      <span className="ml-auto text-sm font-medium tabular-nums text-fg-strong">{count}</span>
    </div>
  );
};

const TableUploadReport: React.FC<Props> = ({ data, onRetry, onClose }) => {
  const { t } = useTranslation();
  const {
    attempted, uploadedCount, skipped, failed, retryFiles,
    processingFailedBatches, serverError,
  } = data;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main px-4 py-4 space-y-4">
        {/* The count, first: how much of what you picked is actually there. */}
        <Well className="space-y-2">
          <Tally
            icon={<PiCheckCircleLight />}
            label={t('Uploaded')}
            count={uploadedCount}
            tone="success"
          />
          {skipped.length > 0 && (
            <Tally
              icon={<PiWarningCircleLight />}
              label={t('Skipped')}
              count={skipped.length}
              tone="warning"
            />
          )}
          {failed.length > 0 && (
            <Tally
              icon={<PiXCircleLight />}
              label={t('Failed')}
              count={failed.length}
              tone="danger"
            />
          )}
          <p className="border-t border-hairline-soft pt-2 text-xs text-fg-muted">
            {t('of')} {attempted} {t('files selected')}
          </p>
        </Well>

        {serverError && (
          <div className="rounded-md border border-danger-200 bg-danger-50 px-3 py-2 text-xs text-danger-700
                          dark:border-danger-800 dark:bg-danger-900/20 dark:text-danger-300">
            {serverError}
          </div>
        )}

        {/* Uploaded, but never queued for AI. The rows are there and empty —
            re-uploading would duplicate them, so say it and stop. */}
        {processingFailedBatches > 0 && (
          <Well className="space-y-1">
            <div className="flex items-center gap-2">
              <PiHourglassLight className="h-4 w-4 flex-shrink-0 text-warning-500" />
              <span className="text-sm text-fg-strong">{t('Not queued for processing')}</span>
            </div>
            <p className="text-xs leading-relaxed text-fg-muted">
              {t('Some uploaded documents were not accepted for analysis. Their rows are in the table — use Continue on the table to process them. Do not re-upload: the files are already there.')}
            </p>
          </Well>
        )}

        {failed.length > 0 && (
          <div className="space-y-2">
            <p className="text-[11px] font-medium uppercase tracking-wide text-fg-muted">
              {t('Failed to upload')}
            </p>
            <Well className="max-h-56 space-y-1 overflow-y-auto custom-scrollbar-main">
              {failed.map((name, i) => (
                <p key={`${name}-${i}`} className="truncate text-xs text-fg-body" title={name}>
                  {name}
                </p>
              ))}
            </Well>
          </div>
        )}

        {skipped.length > 0 && (
          <div className="space-y-2">
            <p className="text-[11px] font-medium uppercase tracking-wide text-fg-muted">
              {t('Skipped by the server')}
            </p>
            <Well className="max-h-56 space-y-2 overflow-y-auto custom-scrollbar-main">
              {skipped.map((s, i) => (
                <div key={`${s.file_name}-${i}`} className="min-w-0">
                  <p className="truncate text-xs text-fg-body" title={s.file_name}>{s.file_name}</p>
                  {s.reason && <p className="text-[11px] text-fg-muted">{s.reason}</p>}
                </div>
              ))}
            </Well>
          </div>
        )}
      </div>

      <div className="glass-bar flex flex-shrink-0 items-center justify-between gap-2 border-t border-hairline px-4 py-3">
        {/*
          Only the transport failures. A skipped file was refused on content and
          would be refused again; a file we cannot match back to its `File`
          object cannot be re-sent from here at all, so the count is honest
          about how many will actually go.
        */}
        {retryFiles.length > 0 ? (
          <Button
            variant="primary"
            size="small"
            icon={<PiArrowsClockwiseLight className="h-4 w-4" />}
            onClick={() => onRetry(retryFiles)}
          >
            {t('Retry failed files')}
            <Badge color="gray" className="ml-2">{retryFiles.length}</Badge>
          </Button>
        ) : (
          <span className="text-xs text-fg-muted">
            {failed.length > 0 ? t('These files must be added again from your computer.') : ''}
          </span>
        )}
        <Button variant="neutral" size="small" onClick={onClose}>
          {t('Close')}
        </Button>
      </div>
    </div>
  );
};

export default TableUploadReport;
